import { Component, OnInit } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { getToken, onMessage, Messaging } from '@angular/fire/messaging';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  title = 'pesca-preco-web';
  tokenToken: string;

  constructor(private router: Router, private swPush: SwPush, private messaging: Messaging) {}

  ngOnInit() {
    this.jivoChat();
    this.requestPermissionAndGetToken();
    this.receivePushMessages();
  }

  /**
   * @description Detectar evento de mudança na rota da aplicação
   *
   * Este método detecta mudanças na rota da aplicação e chama o método jivoChat
   * para aplicar regras de exibição do widget do Jivo Chat.
   *
   * @author Nathan Lira
   */
  routerChange() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.jivoChat();
      });
  }

  /**
   * @description Aplicar regra de exibição do widget do Jivo Chat
   *
   * Este método aplica regras de exibição do widget do Jivo Chat com base na URL atual.
   * Adiciona ou remove a classe 'jvadd' ao elemento body dependendo da rota.
   *
   * @author Nathan Lira
   */
  jivoChat() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const currentUrl: string = event.url;

        if (currentUrl !== '/private/dashboard/home') {
          document.getElementsByTagName('body')[0].classList.add('jvadd');
          if (currentUrl === '/private/dashboard')
            document.getElementsByTagName('body')[0].classList.remove('jvadd');
        } else
          document.getElementsByTagName('body')[0].classList.remove('jvadd');
      }
    });
  }

  /**
   * @description Solicitar permissão e obter token de notificação
   *
   * Este método solicita a permissão do usuário para enviar notificações push.
   * Se a permissão for concedida, ele solicita uma assinatura de push e obtém o token.
   * O token é então enviado para o backend.
   *
   * @author Josemalyson Oliveira
   */
  requestPermissionAndGetToken() {
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        console.log('Notification permission granted.');
        this.getFirebaseMessagingToken();
      } else {
        console.log('Notification permission denied.');
      }
    });
  }

  async getFirebaseMessagingToken() {
    try {
      const token = await getToken(this.messaging, {
        vapidKey: environment.firebase.vapidKey,
      });
      if (token) {
        this.tokenToken = token;
        this.enviarTokenParaBackend(token);
      } else {
        console.log('No registration token available. Request permission to generate one.');
      }
    } catch (error) {
      console.error('An error occurred while retrieving token. ', error);
    }
  }

  /**
   * @description Enviar token para o backend
   *
   * Este método envia o token de notificação para o backend.
   * Deve ser implementada uma chamada HTTP para enviar o token.
   *
   * @param token O token de notificação a ser enviado
   * @author Josemalyson Oliveira
   */
  private enviarTokenParaBackend(token: string) {
    // TODO: Implementar chamada HTTP para enviar o token para o backend
    // Exemplo:
    // this.http.post('/api/notifications/register-token', { token }).subscribe(
    //   () => console.log('Token enviado com sucesso'),
    //   error => console.error('Erro ao enviar token:', error)
    // );
  }

  /**
   * @description Receber mensagens push
   *
   * Este método lida com mensagens push recebidas enquanto o aplicativo está em primeiro plano.
   * Exibe uma notificação com base nos dados recebidos e trata o clique na notificação.
   *
   * @author Josemalyson Oliveira
   */
  receivePushMessages() {
    onMessage(this.messaging, (payload) => {
      console.log('Mensagem recebida em foreground:', payload);

      if (Notification.permission === 'granted') {
        const notificationTitle = payload.notification.title + ' - ' + "Em foreground";
        const notificationOptions: NotificationOptions = {
          body: payload.notification.body,
          icon: payload.notification.icon || 'favicon.ico',
          tag: 'temango-notification',
          requireInteraction: true,
          silent: true, // Desativar som da notificação, pois usaremos um som personalizado
          data: {
            url: payload.data?.url || '/',
            action1Url: payload.data?.action1Url || 'https://www.google.com',
            action2Url: payload.data?.action2Url || 'https://temrango.com.br/'
          },
          badge: 'favicon.ico',
          lang: 'pt-BR',
        };

        const notification = new Notification(notificationTitle || 'Nova Mensagem', notificationOptions);
        console.log(notification);

        // Reproduzir som em loop até que a notificação seja clicada
        const audio = new Audio('assets/mp3/order-sound.mp3');
        audio.loop = true;
        audio.play();

        notification.onclick = (event) => {
          console.log('Clicou na notificação');
          event.preventDefault();
          window.focus();

          // Parar o som quando a notificação for clicada
          audio.pause();
          audio.currentTime = 0;

          const notificationData = notification.data;
          if (notificationData && notificationData.url) {
            window.location.href = notificationData.url;
          }

          notification.close();
        };
      }
    });
  }
}
